import React, { useCallback, useEffect, useState } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { useMessageComposer } from "../../contexts/MessageComposerContext";
import { UserProfile } from "../../components/profile";
import { Modal } from "../../components/modal/dialog";
import classNames from "../../utils/classNames";
import { API, MESSAGE_TYPES, formatDateTime } from "./messageUtils";
import { RecipientStatus } from "./details";

const REFRESH_MS = 60 * 1000;
const WINDOW_MS = 24 * 60 * 60 * 1000;
const FACES = 3;

// Dashboard card: every message from the last 24 hours, newest first, like
// the rest of the Dashboard. Each shows who it went to: a green check on a
// face means confirmed (or, for a message that didn't ask for confirmation,
// read). Follows the Dashboard filters: `siteIds` keeps messages for those
// sites (plus ones sent to everyone clocked in) and `userIds` keeps messages
// sent to those guards, counting only them. Refreshes every minute.
function ActiveMessages({ siteIds = [], userIds = [] }) {
  const authFetch = useAuthFetch();
  const { openMessage, savedAt } = useMessageComposer();
  const [messages, setMessages] = useState([]);
  const [showingAll, setShowingAll] = useState(null);

  const siteKey = siteIds.join(",");
  const userKey = userIds.join(",");

  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        since: new Date(Date.now() - WINDOW_MS).toISOString(),
      });
      if (siteKey) params.set("site_ids", siteKey);
      if (userKey) params.set("user_ids", userKey);
      const response = await authFetch(`${API}/v1/messages?${params}`);
      if (!response.ok) return;
      const recent = await response.json();

      // Who each one went to, limited to the selected guards. Those still
      // outstanding first, so the faces shown are the people to chase.
      const selected = userKey ? new Set(userKey.split(",").map(Number)) : null;
      const withPeople = await Promise.all(
        recent.map(async (m) => {
          const res = await authFetch(`${API}/v1/messages/${m.id}/recipients`);
          const people = res.ok ? await res.json() : [];
          const shown = selected
            ? people.filter((p) => selected.has(p.user_id))
            : people;
          shown.sort((a, b) => isDone(m, a) - isDone(m, b));
          return { ...m, recipients: shown };
        }),
      );
      setMessages(withPeople);
    } catch (error) {
      console.error("Error fetching messages", error);
    }
  }, [authFetch, siteKey, userKey]);

  useEffect(() => {
    fetchMessages();
    const timer = setInterval(fetchMessages, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchMessages, savedAt]);

  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg overflow-hidden p-3">
      <h3 className="text-sm tracking-wide text-gray-500 dark:text-gray-400 pb-2">
        Messaging
      </h3>
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40">
          <ChatBubbleLeftRightIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
            No messages in the last 24 hours
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {messages.map((message) => (
            <MessageTile
              key={message.id}
              message={message}
              onOpen={() => openMessage(message.id, { tab: "Recipients" })}
              onShowAll={() => setShowingAll(message)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={!!showingAll}
        onClose={() => setShowingAll(null)}
        title={showingAll ? progressText(showingAll) : ""}
      >
        <ul className="max-h-96 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
          {(showingAll?.recipients || []).map((r) => (
            <li
              key={r.user_id}
              className="flex items-center justify-between py-3"
            >
              <div className="flex items-center">
                <Face person={r} done={isDone(showingAll, r)} />
                <span className="ml-3 text-sm text-gray-800 dark:text-gray-100">
                  {r.first_name} {r.last_name}
                </span>
              </div>
              <RecipientStatus
                recipient={r}
                needsConfirm={showingAll.requires_ack}
              />
            </li>
          ))}
        </ul>
      </Modal>
    </div>
  );
}

// A recipient is done once they've confirmed, or read it when the message
// didn't ask for confirmation.
const isDone = (message, person) =>
  !!(message.requires_ack ? person.acknowledged_at : person.read_at);

// "2 of 6 confirmed" / "All 6 confirmed" / "Read by 3 of 6".
const progressText = (m) => {
  const total = m.recipient_count;
  if (!total) return "No guards were clocked in";
  if (m.requires_ack) {
    return m.acknowledged_count >= total
      ? `All ${total} confirmed`
      : `${m.acknowledged_count} of ${total} confirmed`;
  }
  return m.read_count >= total
    ? `Read by all ${total}`
    : `Read by ${m.read_count} of ${total}`;
};

function MessageTile({ message, onOpen, onShowAll }) {
  const def = MESSAGE_TYPES[message.type] || MESSAGE_TYPES.general;
  const Icon = def.icon;
  const isBolo = message.type === "bolo";
  const people = message.recipients || [];
  const extra = people.length - FACES;
  // A BOLO is red only while guards still need to confirm it.
  const waiting =
    message.requires_ack &&
    message.acknowledged_count < message.recipient_count;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpen()}
      className="cursor-pointer rounded-md bg-white dark:bg-gray-900 p-3 text-left transition-colors duration-150 hover:bg-gray-100 dark:hover:bg-white/[0.06]"
    >
      <div className="flex items-start gap-3">
        <Icon
          className={classNames(
            "mt-0.5 h-5 w-5 shrink-0",
            isBolo && waiting ? "text-danger" : "text-gray-400",
          )}
        />
        <p className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
          {isBolo ? `BOLO: ${message.title}` : message.title}
        </p>
        <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
          {formatDateTime(message.created_at)}
        </span>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <div className="flex -space-x-2">
          {people.slice(0, FACES).map((p) => (
            <Face key={p.user_id} person={p} done={isDone(message, p)} ring />
          ))}
        </div>
        {extra > 0 && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShowAll();
            }}
            className="text-xs font-semibold text-brand-primary hover:text-brand-200"
          >
            +{extra} more
          </button>
        )}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {progressText(message)}
        </span>
      </div>
    </div>
  );
}

// A guard's picture (or initials) with a green check when they're done.
function Face({ person, done, ring }) {
  return (
    <span
      className="relative inline-block"
      title={`${person.first_name} ${person.last_name}`}
    >
      <UserProfile
        imageUrl={person.profile_url}
        size="small"
        firstName={person.first_name}
        lastName={person.last_name}
        useInitials={!person.profile_url}
        spacing=""
        className={ring ? "ring-2 ring-white dark:ring-gray-900" : ""}
      />
      {done && (
        <span className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success ring-2 ring-white dark:ring-gray-900">
          <CheckIcon className="h-2.5 w-2.5 text-white" aria-hidden="true" />
          <span className="sr-only">Done</span>
        </span>
      )}
    </span>
  );
}

export { ActiveMessages };
